import React, { useState, useEffect, useRef } from 'react'
import { useHistory, NavLink, useParams } from 'react-router-dom'
import axios from 'axios'
import swal from 'sweetalert2'
import Preloader from 'src/views/preloader/Preloader'
import { v4 as uuidv4 } from 'uuid'
import useUnsavedChangesWarning from 'src/hooks/UnsavedChangesWarning'
import { xeroCodeGenerator } from './utils/Functions'

// services
import { getOrderTypeSingle } from 'src/services'
import { getCachedXeroContacts, getXeroContactsArray } from 'src/services/xeroContactsCache'
import { getXeroPrice } from 'src/services/xeroPrice'
import { postXeroRequest } from 'src/services/xeroRequest'

import OrderStatusSection from 'src/components/order/OrderStatusSection'
import CustomerInfoSection from 'src/components/order/CustomerInfoSection'
import OrderDetailsSection from 'src/components/order/OrderDetailsSection'
import ProductInfoSection from 'src/components/order/ProductInfoSection'
import AdditionalLineItemsSection from 'src/components/order/AdditionalLineItemsSection'

const ORDERS_CACHE_MUTATION_KEY = 'orders_cache_mutation'
const ORDERS_CACHE_CHANNEL = 'orders_cache_channel'

const getOrdersTabId = () => {
  let tabId = sessionStorage.getItem('orders_tab_id')

  if (!tabId) {
    tabId = `${Date.now()}_${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('orders_tab_id', tabId)
  }

  return tabId
}

function Create(props) {
  const [Prompt, setDirty, setPristine] = useUnsavedChangesWarning()
  const history = useHistory()
  const ref = useRef()
  let { id } = useParams()

  const [loading, setLoading] = useState(true)
  const [priceLoaded, setPriceLoaded] = useState(false)

  const [Code, setCode] = useState({
    order_type: '',
    machine_id: '',
    productMix: '',
    custom_spread_rate: '',
    sku_location: '',
    location: '',
    unit: '',
  })

  const [spreadchargerates, setSpreadChargeRates] = useState([])
  const [quantityType, setQuantityType] = useState([])
  const [skuLocation, setSkuLocation] = useState([])
  const [productMixList, setProductMixList] = useState([])
  const [productMix, setProductMix] = useState([])
  const [customer, setCustomer] = useState([])
  const [customerSelectedOptions, setCustomerSelectedOptions] = useState(null)
  const [customerAddress, setCustomerAddress] = useState([])
  const [addressSelect, setAddressSelect] = useState([])
  const [agents, setAgents] = useState([])
  const [orderType, setOrderType] = useState([])
  const [subOrderType, setSubOrderType] = useState([])
  const [orderDeliveryZone, setDeliveryZone] = useState([])
  const [orderTypeName, setOrderTypeName] = useState()
  const [machines, setMachines] = useState([])
  const [crops, setCrops] = useState([])
  const [operator, setOperator] = useState([])
  const [lineItems, setLineItems] = useState([])
  const [xeroContact, setXeroContact] = useState([])
  const [OrderStatus, setOrderStatus] = useState('In Progress')

  // IMPORTANT: must be object, not array
  const [errorList, setError] = useState({})

  const [inputList, setInputList] = useState({
    custom_spread_rate: '',
    tons_ordered: '',
    tons_actual: '',
    created_by: '',
    user_name: '',
    agent_id: '',
    order_status: 0,
    order_type: '',
    sub_order_type: '',
    order_delivery_zone: '',
    sku_location: '',
    has_spread_charge_rate: '',
    machine_id: '',
    scheduled_date: '',
    scheduled_note: '',
    operators: [{ operators_id: '' }],
    order_note: '',
    dockets_ready: '0',
    dockets_ready_time: null,
    tons_complete: '0',
    tons_complete_time: null,
    dockets_complete: '0',
    dockets_complete_time: null,
    customer_id: '',
    customer_address: '',
    account_name: '',
    account_contact_name: '',
    terms: '',
    customer_terms_name: '',
    customer_terms_highlight: '',
    customer_reference: '',
    quoted_price: '',
    driver_note: '',
    admin_note: '',
    product_information: [{ docket_numbers: [], product_id: null, order_list_products: [] }],
    other_line_items: [],
    product_mix: [],
    mix_code: '',
    is_xero: '',
  })

  const [spreadList, setSpreadList] = useState({ machine: '', location: '', spreadRate: '' })
  const [xeroItemCodeStatus, setXeroItemCodeStatus] = useState(0)

  const normalizeErrors = (errors = {}) => {
    const formatted = {}

    if (!errors) return formatted

    if (typeof errors === 'string') {
      formatted.general = errors
      return formatted
    }

    Object.keys(errors || {}).forEach((key) => {
      const value = errors[key]

      if (Array.isArray(value)) {
        formatted[key] = value[0]
      } else {
        formatted[key] = value
      }
    })

    return formatted
  }

  const showValidationWarning = async () => {
    await swal.fire({
      icon: 'warning',
      title: 'Required Fields Missing',
      text: 'Please fill all required fields marked with a red star (*).',
    })
  }

  const scrollToErrorSummary = () => {
    setTimeout(() => {
      const errorSummary = document.querySelector('[data-error-summary="true"]')
      if (errorSummary) {
        errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 200)
  }

  const handleValidationError = async (error) => {
    const errors = error?.response?.data?.errors || error?.response?.data?.error || {}
    const formattedErrors = normalizeErrors(errors)

    setError(formattedErrors)
    await showValidationWarning()
    scrollToErrorSummary()
  }

  const validateRequiredFields = () => {
    const errors = {}

    if (!inputList.order_type) {
      errors.order_type = 'Order Type is required'
    }

    if (!inputList.order_delivery_zone) {
      errors.order_delivery_zone = 'Order Delivery Zone is required'
    }

    if (Object.keys(errors).length > 0) {
      setError(errors)
      showValidationWarning()
      scrollToErrorSummary()
      return false
    }

    setError({})
    return true
  }

  const stripHeavyProductInformationForCache = (productInformation = []) => {
    if (!Array.isArray(productInformation)) return []

    return productInformation.map((group) => ({
      id: group?.id,
      product_id: group?.product_id ?? null,

      docket_numbers: Array.isArray(group?.docket_numbers)
        ? group.docket_numbers.map((docket) => ({
            id: docket?.id,
            docket_number: docket?.docket_number || '',
            docket_date: docket?.docket_date || '',
            sku_location: docket?.sku_location || '',
            weight: docket?.weight || '',
          }))
        : [],

      order_list_products: Array.isArray(group?.order_list_products)
        ? group.order_list_products.map((product) => ({
            id: product?.id,
            product_mix_id: product?.product_mix_id ?? null,
            product_name: product?.product_name || '',
            is_customer_product: product?.is_customer_product ?? 0,
            spread_charge_rate: product?.spread_charge_rate || '',
            quantity: product?.quantity || '',
            quantity_type: product?.quantity_type || '',
            total_quantity: product?.total_quantity || '',
            mix_percentage: product?.mix_percentage || '',
            xero_code: product?.xero_code || '',
            unit_price: product?.unit_price || '',
            is_dummy: product?.is_dummy || false,
          }))
        : [],
    }))
  }

  const getCurrentUserIdForMutation = () => {
    return (
      window.__currentUserId ||
      localStorage.getItem('current_user_id') ||
      inputList.created_by ||
      'unknown'
    )
  }

  const broadcastOrdersCacheMutation = ({ type, id: mutationId, patch = null }) => {
    if (!type || !mutationId) return

    const mutationPayload = {
      type,
      id: mutationId,
      patch,
      user_id: getCurrentUserIdForMutation(),
      source_tab_id: getOrdersTabId(),
      timestamp: Date.now(),
    }

    // Same browser / other tabs only.
    // Cross-user / other PC sync is now handled by Laravel SSE from OrderController.
    // Do NOT POST /api/orders-mutations here, otherwise you can get duplicate mutation events.
    localStorage.setItem(ORDERS_CACHE_MUTATION_KEY, JSON.stringify(mutationPayload))

    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const channel = new BroadcastChannel(ORDERS_CACHE_CHANNEL)
        channel.postMessage(mutationPayload)
        channel.close()
      } catch (error) {
        console.debug('[Orders Sync] BroadcastChannel failed:', error.message)
      }
    }
  }

  const buildOrderCachePatch = (nextId = id) => {
    const selectedMachine = machines.find(
      (item) => String(item.id) === String(inputList.machine_id),
    )

    const selectedDeliveryZone = orderDeliveryZone.find(
      (item) => String(item.id) === String(inputList.order_delivery_zone),
    )

    const selectedOperators = inputList.operators
      ?.map((row) => {
        const selectedOperator = operator.find((op) => String(op.id) === String(row.operators_id))

        if (!selectedOperator) return row

        return {
          ...row,
          operator: selectedOperator,
        }
      })
      .filter(Boolean)

    return {
      id: nextId,
      order_status: inputList.order_status,
      dockets_ready: inputList.dockets_ready,
      dockets_complete: inputList.dockets_complete,
      tons_complete: inputList.tons_complete,
      is_xero: inputList.is_xero,
      created_at: inputList.created_at,
      updated_at: new Date().toISOString(),
      tons_ordered: inputList.tons_ordered,
      tons_actual: inputList.tons_actual,
      machine_id: inputList.machine_id,
      customer_address: inputList.customer_address,
      order_note: inputList.order_note,

      customer: {
        code: customerSelectedOptions?.label?.split('|')?.[0]?.trim() || '',
        account_name:
          inputList.account_name || customerSelectedOptions?.label?.split('|')?.[1]?.trim() || '',
        contact_id: customerSelectedOptions?.value || '',
        customer_address: customerAddress || [],
      },

      machine: selectedMachine
        ? {
            name: selectedMachine.name,
            machine_code: selectedMachine.machine_code,
          }
        : null,

      orderdeliveryzone: selectedDeliveryZone
        ? {
            name: selectedDeliveryZone.name,
          }
        : null,

      order_list: stripHeavyProductInformationForCache(inputList.product_information),
      operators: selectedOperators || inputList.operators,
      order_operators: selectedOperators || inputList.operators,
    }
  }

  const xeroCodeGeneratorMix = () => {
    let mc = machines?.find((x) => x.id == inputList.machine_id)
    let sku = skuLocation?.find((x) => x.id == inputList.sku_location)

    return (
      (mc?.machine_code ? mc?.machine_code : 'N/A') +
      'MIX.' +
      (mc?.machine_code ? sku?.xero_code : 'N/A')
    )
  }

  const xeroSkuGeneratorMix = () => {
    const products = inputList.product_information[0]?.order_list_products.filter(
      (product) => product.product_name !== 'FRE' && !product.is_dummy,
    )

    let totalProportionatePrice = 0

    products?.forEach((product) => {
      const mixPercentage = parseFloat(product.mix_percentage) || 0
      const unitPrice = parseFloat(product.unit_price) || 0
      totalProportionatePrice += (mixPercentage / 100) * unitPrice
    })

    return totalProportionatePrice.toFixed(3)
  }

  const isValidXeroValue = (value) => {
    const normalized = value === null || value === undefined ? '' : String(value).trim()
    return normalized !== '' && normalized !== 'N/A' && normalized !== 'NaN'
  }

  // IMPORTANT:
  // Do not call Xero when clicking Complete.
  // Complete should only validate the xero_code/unit_price already loaded in the form.
  // Calling /api/get_xero_price here can trigger Xero 429 rate-limit errors.
  const getCodeValueAfterCheck = async () => {
    const productGroups = Array.isArray(inputList.product_information)
      ? inputList.product_information
      : []

    const allProducts = productGroups.flatMap((group) =>
      Array.isArray(group?.order_list_products) ? group.order_list_products : [],
    )

    const realProducts = allProducts.filter((p) => !p?.is_dummy && p?.product_name !== 'FRE')

    const additionalItems = Array.isArray(inputList.other_line_items)
      ? inputList.other_line_items
      : []

    const usedAdditionalItems = additionalItems.filter(
      (item) => item?.type || item?.xero_code || item?.description || item?.amount,
    )

    if (!realProducts.length && !usedAdditionalItems.length) {
      setXeroItemCodeStatus(0)
      return {
        ok: false,
        message: 'Add at least one product or additional line item before completing the order.',
      }
    }

    if (realProducts.length > 1) {
      const mixCode = xeroCodeGeneratorMix()
      const mixPrice = xeroSkuGeneratorMix()

      if (!isValidXeroValue(mixCode) || Number(mixPrice) <= 0) {
        setXeroItemCodeStatus(0)
        return {
          ok: false,
          message:
            'Mix Xero code or mix unit price is missing. Please update/load the unit price before completing.',
        }
      }
    }

    const invalidProduct = realProducts.find(
      (product) => !isValidXeroValue(product?.xero_code) || !isValidXeroValue(product?.unit_price),
    )

    if (invalidProduct) {
      setXeroItemCodeStatus(0)
      return {
        ok: false,
        message:
          'Product Xero code or unit price is missing. Please update/load the unit price before completing.',
      }
    }

    const invalidLineItem = usedAdditionalItems.find(
      (item) => !item?.type || !isValidXeroValue(item?.xero_code),
    )

    if (invalidLineItem) {
      setXeroItemCodeStatus(0)
      return {
        ok: false,
        message:
          'Additional line item Xero code is missing. Please reselect the additional line item after the Xero limit resets, then save.',
      }
    }

    setXeroItemCodeStatus(1)
    return { ok: true }
  }

  const HandleCompleted = async (e) => {
    const checked = e.target.checked

    setDirty()

    if (Number(inputList.order_status) === 3) {
      swal.fire('Error', 'Cancelled orders cannot be marked as complete.', 'error')
      return
    }

    if (!checked) {
      setInputList((prev) => ({ ...prev, order_status: 0 }))
      return
    }

    if (inputList.tons_complete !== '1' || inputList.dockets_complete !== '1') {
      swal.fire(
        'Error',
        'Both "Tons Complete" and "Dockets Complete" must be checked before completing the order.',
        'error',
      )
      return
    }

    const completeValidation = await getCodeValueAfterCheck()

    if (!completeValidation.ok) {
      swal.fire(
        'Error',
        completeValidation.message || 'Xero code or unit price is missing.',
        'error',
      )

      setInputList((prev) => ({
        ...prev,
        order_status: 0,
      }))

      return
    }

    setInputList((prev) => ({
      ...prev,
      order_status: 1,
    }))

    swal.fire('Success', 'Order marked complete', 'success')
  }

  const HandleCancelled = (e) => {
    setDirty()

    const checked = e.target.checked

    if (checked) {
      swal
        .fire({
          title: 'Cancel this order?',
          text: 'Cancelling will reset Tons Complete, Dockets Complete, and Completed status.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, Cancel Order',
        })
        .then((result) => {
          if (result.isConfirmed) {
            setInputList((prev) => ({
              ...prev,
              order_status: 3,
              dockets_ready: '0',
              dockets_ready_time: null,
              tons_complete: '0',
              tons_complete_time: null,
              dockets_complete: '0',
              dockets_complete_time: null,
            }))

            setOrderStatus('Cancelled')
            swal.fire('Cancelled!', 'Order has been marked as cancelled.', 'success')
          }
        })

      return
    }

    swal
      .fire({
        title: 'Remove cancelled status?',
        text: 'This will reopen the order.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Reopen',
      })
      .then((result) => {
        if (result.isConfirmed) {
          setInputList((prev) => ({
            ...prev,
            order_status: 0,
          }))

          setOrderStatus('')
          swal.fire('Reopened!', 'Cancelled status has been removed.', 'success')
        } else {
          setInputList((prev) => ({
            ...prev,
            order_status: 3,
          }))
        }
      })
  }

  const loadPrice = async () => {
    setPriceLoaded(true)

    const originalProductInformation = inputList.product_information
    let order_status = inputList.order_status

    const newProductInformation = originalProductInformation.map((group) => {
      const updatedProducts = group.order_list_products.map((product) => {
        const xero_code = xeroCodeGenerator(
          skuLocation,
          orderType,
          machines,
          product.product_name,
          spreadchargerates,
          product.spread_charge_rate,
          quantityType,
          product.quantity_type,
          inputList,
        )

        return { ...product, xero_code }
      })

      return { ...group, order_list_products: updatedProducts }
    })

    const priceFetchTargets = []

    newProductInformation.forEach((group, gi) => {
      group.order_list_products.forEach((product, pi) => {
        if (
          product.xero_code &&
          product.xero_code !== 'N/A' &&
          !product.is_dummy &&
          product.product_name !== 'FRE'
        ) {
          priceFetchTargets.push({ code: product.xero_code, gi, pi })
        }
      })
    })

    const priceResults = await Promise.allSettled(
      priceFetchTargets.map((target) => getXeroPrice(target.code)),
    )

    let failed = false

    priceFetchTargets.forEach((target, idx) => {
      const { gi, pi } = target
      const result = priceResults[idx]
      let unit_price = 'N/A'

      if (result.status === 'fulfilled') {
        unit_price = result.value?.data?.item?.body?.Items[0]?.SalesDetails?.UnitPrice || 'N/A'
      } else {
        failed = true
      }

      newProductInformation[gi].order_list_products[pi].unit_price = unit_price
    })

    newProductInformation.forEach((group) => {
      group.order_list_products.forEach((product) => {
        if (!product.xero_code || product.xero_code === 'N/A') {
          product.unit_price = 'N/A'
          failed = true
        }
      })
    })

    setXeroItemCodeStatus(failed ? 0 : 1)

    if (failed && Number(order_status) !== 3) {
      order_status = 0
    }

    setInputList((prev) => ({
      ...prev,
      product_information: newProductInformation,
      order_status,
    }))

    setPriceLoaded(false)
  }

  useEffect(() => {
    const fetchBaseData = async () => {
      setLoading(true)

      try {
        const baseOrdersRequest = axios.get('/api/orders')
        const userOrOrderRequest = id
          ? axios.get(`api/edit_orders/${id}`)
          : axios.get('/api/edit_profile')

        const [{ data: ordersData }, userOrOrderPromise] = await Promise.all([
          baseOrdersRequest,
          userOrOrderRequest,
        ])

        if (ordersData?.status === 200) {
          const {
            agent,
            order_types,
            sub_order_type,
            delivery_zones,
            machine,
            crop,
            operator,
            sku_location,
            quantity,
            spread_charge,
            productmix,
            products,
            line_item,
          } = ordersData

          setAgents(agent)
          setOrderType(order_types)
          setSubOrderType(sub_order_type)
          setDeliveryZone(delivery_zones)
          setMachines(machine)
          setCrops(crop)
          setOperator(operator)
          setSkuLocation(sku_location)
          setQuantityType(quantity)
          setSpreadChargeRates(spread_charge)
          setProductMix(products)
          setLineItems(line_item)

          if (productmix) {
            setProductMixList(productmix.map((d) => ({ value: d.id, label: d.product_docket })))
          }

          if (products) {
            setProductMixList((prev) => [
              ...prev,
              ...products.map((d) => ({ value: d.id, label: d.product_code })),
            ])
          }
        }

        if (id) {
          const { data: editOrderData } = userOrOrderPromise
          const { orderUpdate: editOrders, orders } = editOrderData

          let product_information = editOrders.product_information?.length
            ? editOrders.product_information
            : [{ docket_numbers: [], product_id: null, order_list_products: [] }]

          const { data: customerData } = await axios.get(
            `api/edit_customers_orders/${orders.customer_id}`,
          )

          const orderTypeRes = getOrderTypeSingle(editOrders.order_type)

          if (editOrderData.status === 200) {
            const customer = customerData.customer

            setCustomerSelectedOptions({
              value: customer.id,
              label: `${customer.code} | ${customer.account_name}`,
            })

            setInputList((prev) => ({
              ...prev,
              customer_id: customer.id,
              account_name: customer.account_name,
              product_information: [...product_information],
              account_contact_name: customer.account_contact_name,
              customer_terms_name: customer.customer_terms?.name,
              customer_terms_highlight: customer.customer_terms?.highlight,
              is_xero: editOrders.is_xero,
              ...orders,
              operators:
                Array.isArray(orders.operators) && orders.operators.length > 0
                  ? orders.operators
                  : [{ operators_id: '' }],
            }))

            setCustomerAddress(customer.customer_address)
          }

          if (editOrders.id) {
            fetchCustomerXero(editOrders.customer.contact_id)
          }

          const orderTypeNameRes = await orderTypeRes

          if (orderTypeNameRes?.data?.order_types?.name) {
            setOrderTypeName(orderTypeNameRes.data.order_types.name)
          }
        } else {
          const { data: userData } = userOrOrderPromise

          if (userData.status === 200) {
            setInputList((prev) => ({
              ...prev,
              created_by: userData.user.id,
              user_name: userData.user.name,
            }))
          }

          fetchCustomerXero()
        }
      } catch (error) {
        console.error('Error fetching base data:', error)
      } finally {
        setLoading(false)
      }
    }

    const fetchCustomerXero = async (contact_id = null) => {
      try {
        const xeroData = await getCachedXeroContacts()
        const contacts = getXeroContactsArray(xeroData)

        setXeroContact(contacts)

        const newCustomers = contacts
          .filter((c) => c.IsCustomer)
          .map((c) => ({
            value: c.ContactID,
            label: `${c.AccountNumber || 'No Account Number'} | ${c.Name}`,
            raw: c,
            ddi: c.Phones?.find((p) => p.PhoneType === 'DDI')?.PhoneNumber || '',
            defaultPhone: c.Phones?.find((p) => p.PhoneType === 'DEFAULT')?.PhoneNumber || '',
            mobile: c.Phones?.find((p) => p.PhoneType === 'MOBILE')?.PhoneNumber || '',
            fax: c.Phones?.find((p) => p.PhoneType === 'FAX')?.PhoneNumber || '',
          }))

        setCustomer(newCustomers)

        if (contact_id) {
          const xeroContactID = contacts.find((c) => c.ContactID === contact_id)

          if (xeroContactID) {
            setAddressSelect(xeroContactID)
          }
        }
      } catch (err) {
        console.error('Error loading Xero contacts:', err)
        setXeroContact([])
        setCustomer([])
      }
    }

    fetchBaseData()
  }, [id])

  useEffect(() => {
    const list = { ...inputList }
    let tons_actual = 0

    list.product_information.forEach((info) =>
      info.docket_numbers.forEach((d) => {
        if (Number(d.weight)) tons_actual += Number(d.weight)
      }),
    )

    list.tons_actual = tons_actual
    setInputList(list)
  }, [JSON.stringify(inputList.product_information)])

  useEffect(() => {
    const { dockets_ready, dockets_complete, tons_complete, order_status } = inputList

    if (Number(order_status) === 3) {
      setOrderStatus('Cancelled')
    } else if (Number(order_status) === 1) {
      setOrderStatus('Complete')
    } else if (
      (tons_complete === '1' && dockets_complete === '1') ||
      (tons_complete === '1' && dockets_complete === '0') ||
      (tons_complete === '0' && dockets_complete === '1')
    ) {
      setOrderStatus('Ready')
    } else if (dockets_ready === '3') {
      setOrderStatus('In Progress')
    } else if (dockets_ready === '1') {
      setOrderStatus('Ready')
    } else if (dockets_ready === '2') {
      setOrderStatus('Will Advise')
    } else {
      setOrderStatus('')
    }
  }, [inputList])

  const prevCompleteRef = useRef(null)
  const didMountRef = useRef(false)

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      prevCompleteRef.current =
        inputList.tons_complete === '1' && inputList.dockets_complete === '1'
      return
    }

    if (Number(inputList.order_status) === 3) {
      return
    }

    const nowComplete = inputList.tons_complete === '1' && inputList.dockets_complete === '1'

    // IMPORTANT:
    // Do not set order_status = 1 just because Tons Complete and Dockets Complete are checked.
    // The order should become Completed only when the user clicks the Complete checkbox.
    // If a completed order is later made incomplete, reset the completed status.
    if (!nowComplete && prevCompleteRef.current && Number(inputList.order_status) === 1) {
      setInputList((prev) => ({
        ...prev,
        order_status: 0,
      }))
    }

    prevCompleteRef.current = nowComplete
  }, [inputList.tons_complete, inputList.dockets_complete, inputList.order_status])

  useEffect(() => {
    ensureDummyProductIfNeeded()
  }, [inputList.other_line_items])

  const handleImage = (e, groupIndex, docketIndex) => {
    setDirty()

    const file = e.target.files?.[0]
    if (!file) return

    const maxSize = 5 * 1024 * 1024

    if (file.size > maxSize) {
      swal.fire({
        icon: 'error',
        title: 'File too large',
        text: 'The selected file exceeds the maximum allowed size of 5MB.',
      })

      e.target.value = ''
      return
    }

    const reader = new FileReader()
    reader.readAsDataURL(file)

    reader.onload = () => {
      const dataUrl = reader.result
      const fileName = file.name

      setInputList((prev) => {
        if (!Array.isArray(prev.product_information)) return prev

        const group = prev.product_information[groupIndex]
        if (!group) return prev

        const dockets = Array.isArray(group.docket_numbers) ? group.docket_numbers : []
        if (!dockets[docketIndex]) return prev

        const next = { ...prev }
        const nextPI = [...prev.product_information]
        const nextGroup = { ...group }
        const nextDockets = [...dockets]
        const nextDocket = { ...nextDockets[docketIndex], imageURL: dataUrl, imageName: fileName }

        nextDockets[docketIndex] = nextDocket
        nextGroup.docket_numbers = nextDockets
        nextPI[groupIndex] = nextGroup
        next.product_information = nextPI

        return next
      })

      e.target.value = ''
    }

    reader.onerror = (err) => {
      console.error('Error reading file:', err)
      swal.fire('Error', 'Failed to read the file.', 'error')
      e.target.value = ''
    }
  }

  const handleChangeCustomerID = async (option) => {
    setDirty()
    setAddressSelect([])
    setCustomerSelectedOptions(option)

    if (!option) {
      setInputList((prev) => ({
        ...prev,
        customer_id: '',
        customer_address: '',
        account_name: '',
        account_contact_name: '',
        customer_terms_name: '',
        customer_terms_highlight: '',
      }))

      setCustomerAddress([])
      return
    }

    try {
      const res = await axios.get(`api/edit_customers/${option.value}`)

      if (res.data.status === 200) {
        const cust = res.data.customer

        setInputList((prev) => ({
          ...prev,
          customer_id: cust.id,
          account_name: cust.account_name,
          account_contact_name: cust.account_contact_name,
          terms: cust.terms,
          customer_terms_name: cust.customer_terms?.name || '',
          customer_terms_highlight: cust.customer_terms?.highlight || '',
          customer_address: '',
        }))

        setCustomerAddress(cust.customer_address || [])

        setError((prev) => {
          const next = { ...prev }
          delete next.customer_id
          return next
        })
      } else {
        setInputList((prev) => ({
          ...prev,
          customer_id: '',
          customer_address: '',
          customer_terms_name: '',
          customer_terms_highlight: '',
        }))

        setCustomerAddress([])
        setAddressSelect([])

        swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Customer details could not be found in local records.',
        })
      }
    } catch (error) {
      setInputList((prev) => ({
        ...prev,
        customer_id: '',
        customer_address: '',
        customer_terms_name: '',
        customer_terms_highlight: '',
      }))

      setCustomerAddress([])
      setAddressSelect([])

      swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'Failed to fetch local customer details.',
      })
    }
  }

  const HandleChangeAddress = async (e) => {
    setDirty()

    const { name, value } = e.target

    setInputList((prev) => ({
      ...prev,
      [name]: value,
    }))

    setError((prev) => {
      const next = { ...prev }
      delete next[name]
      return next
    })

    try {
      const addressDetails = await axios.get(`/api/edit_addresse/${value}`)

      if (addressDetails.data.status === 200) {
        const contact_id = addressDetails.data.address.p_location
        const xeroContactID = xeroContact.find((c) => c.ContactID === contact_id)
        setAddressSelect(xeroContactID || [])
      } else {
        setAddressSelect([])
      }
    } catch (error) {
      setAddressSelect([])
    }
  }

  const handleChangeLevel1 = (e) => {
    const { name, value } = e.target

    setDirty()

    setInputList((prev) => (prev[name] === value ? prev : { ...prev, [name]: value }))

    setError((prev) => {
      if (!prev || !prev[name]) return prev

      const next = { ...prev }
      delete next[name]
      return next
    })
  }

  const handleChangeLevel2 = (e, i, arrayname) => {
    setDirty()

    const { name, value } = e.target

    setInputList((prev) => {
      const arrCopy = prev[arrayname].slice()
      arrCopy[i] = { ...arrCopy[i], [name]: value }
      return { ...prev, [arrayname]: arrCopy }
    })
  }

  const handleChangeLevel3 = async (e, i, j, arrayname, arrayname2) => {
    setDirty()

    const { name, value } = e.target

    setInputList((prev) => {
      const arrayCopy = prev[arrayname].map((item, idx) => {
        if (idx !== i) return item

        const subArr = item[arrayname2].map((sub, sidx) =>
          sidx !== j ? sub : { ...sub, [name]: value },
        )

        return { ...item, [arrayname2]: subArr }
      })

      return { ...prev, [arrayname]: arrayCopy }
    })

    if (['spread_charge_rate', 'quantity_type'].includes(name)) {
      setInputList(async (prev) => {
        const arrayCopy = prev[arrayname].map((item, idx) => {
          if (idx !== i) return item

          const subArr = item[arrayname2].map((sub, sidx) => {
            if (sidx !== j) return sub

            const nextSub = { ...sub, [name]: value }

            const xero_code = xeroCodeGenerator(
              skuLocation,
              orderType,
              machines,
              nextSub.product_name,
              spreadchargerates,
              nextSub.spread_charge_rate,
              quantityType,
              nextSub.quantity_type,
              prev,
            )

            return { ...nextSub, xero_code }
          })

          return { ...item, [arrayname2]: subArr }
        })

        const updatedProduct = arrayCopy[i][arrayname2][j]
        let unit_price = 'N/A'
        let xeroStatus = 0

        if (updatedProduct.xero_code && updatedProduct.xero_code !== 'N/A') {
          try {
            const resp = await getXeroPrice(updatedProduct.xero_code)

            unit_price = resp.data?.item?.body?.Items[0]?.SalesDetails?.UnitPrice || 'N/A'
            xeroStatus = 1
          } catch {
            unit_price = 'N/A'
            xeroStatus = 0
          }
        }

        arrayCopy[i][arrayname2][j] = {
          ...updatedProduct,
          unit_price,
        }

        setXeroItemCodeStatus(xeroStatus)

        return {
          ...prev,
          [arrayname]: arrayCopy,
          order_status: xeroStatus === 0 ? 0 : prev.order_status,
        }
      })
    }
  }

  const handleChangeLevel1XeroCode = async (e) => {
    e.preventDefault()

    const { name, value } = e.target

    setDirty()

    setError((prev) => {
      if (!prev || !prev[name]) return prev

      const next = { ...prev }
      delete next[name]
      return next
    })

    if (value === '' || value === '0') {
      setInputList((prev) => ({ ...prev, [name]: value }))
      return
    }

    let nextInputList = { ...inputList, [name]: value }

    let updatedHasSpreadChargeRate = null

    if (name === 'order_type') {
      try {
        const res = await getOrderTypeSingle(value)

        if (res.data.status === 200) {
          setOrderTypeName(res.data.order_types.name)
          updatedHasSpreadChargeRate = res.data.order_types.has_spread_charge_rate
          nextInputList.has_spread_charge_rate = updatedHasSpreadChargeRate
        } else {
          history.push('/orders')
          return
        }
      } catch (error) {
        history.push('/orders')
        return
      }
    }

    let failed = false

    let newProductInformation = nextInputList.product_information.map((productInfo) => {
      const arr = productInfo.order_list_products
      const spread_rate = arr[0]?.spread_charge_rate

      return {
        ...productInfo,
        order_list_products: arr.map((product) => ({
          ...product,
          xero_code: xeroCodeGenerator(
            skuLocation,
            orderType,
            machines,
            product.product_name,
            spreadchargerates,
            spread_rate,
            quantityType,
            product.quantity_type,
            nextInputList,
          ),
        })),
      }
    })

    const codeMap = []

    newProductInformation.forEach((group, gi) =>
      group.order_list_products.forEach((product, pi) => {
        if (product.xero_code && product.xero_code !== 'N/A') {
          codeMap.push({ gi, pi, code: product.xero_code })
        }
      }),
    )

    let priceResults = []

    if (codeMap.length) {
      priceResults = await Promise.allSettled(codeMap.map(({ code }) => getXeroPrice(code)))
    }

    let priceIdx = 0

    newProductInformation = newProductInformation.map((group) => ({
      ...group,
      order_list_products: group.order_list_products.map((product) => {
        if (product.xero_code && product.xero_code !== 'N/A') {
          const result = priceResults[priceIdx]
          priceIdx++

          if (result?.status === 'fulfilled') {
            return {
              ...product,
              unit_price:
                result.value?.data?.item?.body?.Items[0]?.SalesDetails?.UnitPrice || 'N/A',
            }
          }

          failed = true
          return { ...product, unit_price: 'N/A' }
        }

        failed = true
        return { ...product, unit_price: 'N/A' }
      }),
    }))

    setXeroItemCodeStatus(failed ? 0 : 1)

    let tons_actual = 0

    newProductInformation.forEach((info) =>
      info.docket_numbers.forEach((docket) => {
        tons_actual += Number(docket.weight) || 0
      }),
    )

    const tonsActualFixed = tons_actual.toFixed(3)

    const productLength = newProductInformation[0]?.order_list_products.length
    const mixPrice = parseInt(mixSkuValue)
    const unitPrice = newProductInformation[0]?.order_list_products[0]?.unit_price || 'N/A'
    const resultAmount = productLength > 1 ? mixPrice : unitPrice
    const Amount = tonsActualFixed * resultAmount

    const updatedOtherLineItems = inputList.other_line_items.map((item) => ({
      ...item,
      amount: Amount ? Amount.toFixed(4) : '',
    }))

    setInputList((prev) => ({
      ...prev,
      [name]: value,
      has_spread_charge_rate: updatedHasSpreadChargeRate ?? prev.has_spread_charge_rate,
      product_information: newProductInformation,
      tons_actual: tonsActualFixed,
      other_line_items: updatedOtherLineItems,
      order_status: failed && Number(prev.order_status) !== 3 ? 0 : prev.order_status,
    }))
  }

  const handleChangeLevel3XeroCode = async (e, gi, pi, arrayname, arrayname2) => {
    setDirty()
    setPriceLoaded(true)

    const { name, value } = e.target

    let xeroCode = ''

    setInputList((prev) => {
      const updated = { ...prev }
      updated[arrayname] = [...updated[arrayname]]
      updated[arrayname][gi] = { ...updated[arrayname][gi] }
      updated[arrayname][gi][arrayname2] = [...updated[arrayname][gi][arrayname2]]

      const product = { ...updated[arrayname][gi][arrayname2][pi] }

      if (name === 'quantity_type') {
        product.quantity_type = value
      } else if (name === 'spread_charge_rate') {
        product.spread_charge_rate = value
      }

      product.xero_code = xeroCodeGenerator(
        skuLocation,
        orderType,
        machines,
        product.product_name,
        spreadchargerates,
        product.spread_charge_rate,
        quantityType,
        product.quantity_type,
        updated,
      )

      xeroCode = product.xero_code
      updated[arrayname][gi][arrayname2][pi] = product

      return updated
    })

    setTimeout(async () => {
      let freshPrice = 'N/A'

      if (xeroCode && xeroCode !== 'N/A') {
        try {
          const resp = await getXeroPrice(xeroCode)
          freshPrice = resp.data?.item?.body?.Items[0]?.SalesDetails?.UnitPrice || 'N/A'
        } catch {
          freshPrice = 'N/A'
        }
      }

      setInputList((prev) => {
        const updated = { ...prev }
        updated[arrayname] = [...updated[arrayname]]
        updated[arrayname][gi] = { ...updated[arrayname][gi] }
        updated[arrayname][gi][arrayname2] = [...updated[arrayname][gi][arrayname2]]

        const product = { ...updated[arrayname][gi][arrayname2][pi] }
        product.unit_price = freshPrice

        updated[arrayname][gi][arrayname2][pi] = product

        return updated
      })

      setPriceLoaded(false)
    }, 0)
  }

  const handleBlurDecimalLevel1 = (e) => {
    setDirty()

    const { name, value } = e.target
    const list = { ...inputList }

    list[name] = parseFloat(value).toFixed(3)

    setInputList(list)
  }

  const handleBlurDecimalLevel2 = (e, i, arrayname) => {
    setDirty()

    const { name, value } = e.target
    const list = { ...inputList }

    list[arrayname][i][name] = parseFloat(value).toFixed(4)

    setInputList(list)
  }

  const handleAddNewProduct = async (e, index) => {
    setDirty()
    e.preventDefault()

    e.target.disabled = true
    e.target.innerText = 'Adding...'

    const id = inputList.product_information[index].product_id
    let newData = null

    const insertProduct = (product, isMix) => ({
      product_mix_id: product.id,
      product_name: product.product_code,
      is_customer_product: product.is_customer_product,
      spread_charge_rate: '',
      quantity: '',
      quantity_type: '',
      total_quantity: '',
      mix_percentage: '100',
      xero_code: '',
      unit_price: isMix ? Math.floor(Math.random() * 100) + 1 : 'N/A',
      id: '',
    })

    try {
      const { data: mixData } = await axios.get(`api/edit_product_mix/${id}`)

      if (mixData.status === 200 && mixData.product_mix) {
        newData = insertProduct(mixData.product_mix, true)
      } else {
        const { data: prodData } = await axios.get(`api/edit_products/${id}`)

        if (prodData.status === 200 && prodData.product) {
          const alreadyExists = inputList.product_information[index].order_list_products.some(
            (p) => Number(p.product_mix_id) === prodData.product.id,
          )

          if (alreadyExists) {
            swal.fire('Error', 'Cannot duplicate Product / Product Mix', 'error')
            e.target.disabled = false
            e.target.innerText = 'Add Product'
            return
          }

          newData = insertProduct(prodData.product, false)
        } else {
          swal.fire('Error', "Couldn't Find The Product / Product Mix", 'error')
          e.target.disabled = false
          e.target.innerText = 'Add Product'
          return
        }
      }

      if (newData) {
        setInputList((prev) => {
          const updated = { ...prev }
          updated.product_information = [...prev.product_information]
          updated.product_information[index] = { ...updated.product_information[index] }
          updated.product_information[index].order_list_products = [
            ...updated.product_information[index].order_list_products,
            newData,
          ]

          return updated
        })
      }
    } catch (err) {
      swal.fire('Error', err.message, 'error')
    } finally {
      e.target.disabled = false
      e.target.innerText = 'Add Product'
    }
  }

  const handleAddDocket = (e, groupIndex) => {
    setDirty()
    e.preventDefault()

    setInputList((prev) => {
      const product_information = [...prev.product_information]
      const group = { ...product_information[groupIndex] }
      const dockets = Array.isArray(group.docket_numbers) ? [...group.docket_numbers] : []

      dockets.push({
        id: uuidv4(),
        sku_location: '',
        docket_number: '',
        docket_date: '',
        weight: '',
        imageName: '',
        imageURL: '',
      })

      group.docket_numbers = dockets
      product_information[groupIndex] = group

      return { ...prev, product_information }
    })
  }

  const handleAddOperators = (e) => {
    setDirty()
    e.preventDefault()

    const list = { ...inputList }
    list.operators.push({ operators_id: '' })

    setInputList(list)
  }

  const addLineItems = (e) => {
    setDirty()
    e.preventDefault()

    setInputList((prev) => ({
      ...prev,
      other_line_items: [
        ...prev.other_line_items,
        { type: '', description: '', sku_location: '', quantity: '', amount: '' },
      ],
    }))
  }

  const ensureDummyProductIfNeeded = () => {
    setInputList((prev) => {
      const updated = { ...prev }

      const hasLineItems = updated.other_line_items?.length > 0
      const hasNoProducts =
        updated.product_information?.length === 0 ||
        updated.product_information[0].order_list_products?.length === 0

      if (hasLineItems && hasNoProducts) {
        updated.product_information = [
          {
            docket_numbers: [],
            product_id: null,
            order_list_products: [
              {
                product_mix_id: null,
                product_name: 'FRE',
                is_dummy: true,
                is_customer_product: 0,
                spread_charge_rate: '0',
                quantity: '0',
                quantity_type: '0',
                total_quantity: '0',
                mix_percentage: '0.000',
                xero_code: 'FRE',
                unit_price: '0.00',
                id: '',
              },
            ],
          },
        ]
      }

      return updated
    })
  }

  const handleRemoveDocket = (e, docketIndex, i) => {
    setDirty()
    e.preventDefault()

    const list = { ...inputList }
    list.product_information[i].docket_numbers.splice(docketIndex, 1)

    setInputList(list)
  }

  const removeLineItems = (e, i) => {
    setDirty()
    e.preventDefault()

    const list = { ...inputList }
    list.other_line_items.splice(i, 1)

    setInputList(list)
  }

  const handleRemoveOperators = (e, i) => {
    setDirty()
    e.preventDefault()

    const list = { ...inputList }
    list.operators.splice(i, 1)

    setInputList(list)
  }

  const quantityTotal = () => {
    const list = { ...inputList }

    list.product_information.forEach((info) => {
      const nonFre = info.order_list_products.filter((p) => p.product_name !== 'FRE' && !p.is_dummy)

      let total = 0

      nonFre.forEach((prod) => {
        const qt = quantityType.find((q) => String(q.id) === String(prod.quantity_type))
        const quantityValue = qt?.quantity_value ? Number(qt.quantity_value) : 1
        total += Number(prod.quantity) * quantityValue
      })

      info.total_calculated = total.toFixed(3)

      nonFre.forEach((prod) => {
        const qt = quantityType.find((q) => String(q.id) === String(prod.quantity_type))
        const quantityValue = qt?.quantity_value ? Number(qt.quantity_value) : 1
        const prodTons = Number(prod.quantity) * quantityValue
        prod.mix_percentage = total > 0 ? ((prodTons / total) * 100).toFixed(3) : '0.000'
      })

      const fre = info.order_list_products.filter((p) => p.product_name === 'FRE')
      info.order_list_products = [...nonFre, ...fre]
    })

    list.tons_ordered = list.product_information[0]?.total_calculated || '0.000'

    setInputList(list)
  }

  const percentatgeCalc = () => {
    const list = { ...inputList }

    list.product_information.forEach((info) => {
      const nonFre = info.order_list_products.filter((p) => p.product_name !== 'FRE' && !p.is_dummy)
      const fre = info.order_list_products.filter((p) => p.product_name === 'FRE')

      if (nonFre.length > 1) {
        nonFre.forEach((p) => {
          const percentage = (Number(list.tons_ordered) * Number(p.mix_percentage)) / 100
          p.quantity = Number(percentage).toFixed(3)
        })
      }

      fre.forEach((p) => {
        p.quantity = p.mix_percentage
      })

      info.order_list_products = [...nonFre, ...fre]
    })

    setInputList(list)
  }

  const percentatgeCalcBlur = (e, gi, pi, arrayname, arrayname2) => {
    const { name, value } = e.target

    setDirty()

    setInputList((prev) => {
      if (
        !prev[arrayname] ||
        !prev[arrayname][gi] ||
        !prev[arrayname][gi][arrayname2] ||
        !prev[arrayname][gi][arrayname2][pi]
      ) {
        return prev
      }

      const updated = { ...prev }
      updated[arrayname] = [...updated[arrayname]]
      updated[arrayname][gi] = { ...updated[arrayname][gi] }
      updated[arrayname][gi][arrayname2] = [...updated[arrayname][gi][arrayname2]]

      const product = { ...updated[arrayname][gi][arrayname2][pi] }
      product[name] = Number(value).toFixed(3)

      const tonsOrdered = parseFloat(prev.tons_ordered) || 0
      product.quantity = ((tonsOrdered * parseFloat(value)) / 100).toFixed(3)

      updated[arrayname][gi][arrayname2][pi] = product

      return updated
    })
  }

  const quantityTotalBlur = (e, i, j, arrayname, arrayname2) => {
    const { name, value } = e.target
    const list = { ...inputList }

    list[arrayname][i][arrayname2][j][name] = Number(value).toFixed(3)

    setInputList(list)
  }

  const enableMix = (e) => {
    e.preventDefault()

    document.getElementsByName('tons_ordered')[0]?.removeAttribute('disabled')

    inputList.product_information.forEach((info) => {
      info.order_list_products.forEach((p, idx) => {
        const mixEl = document.getElementsByName('mix_percentage')[idx]
        const qtyEl = document.getElementsByName('quantity')[idx]

        if (p.product_name === 'FRE') {
          mixEl?.setAttribute('disabled', '')
          qtyEl?.setAttribute('disabled', '')
        } else {
          mixEl?.removeAttribute('disabled')
          qtyEl?.setAttribute('disabled', '')

          if (qtyEl) qtyEl.style.color = 'black'
          if (mixEl) mixEl.style.color = 'blue'
        }
      })
    })
  }

  const enableQuantity = (e) => {
    e.preventDefault()

    document.getElementsByName('tons_ordered')[0]?.setAttribute('disabled', '')

    inputList.product_information.forEach((info) => {
      info.order_list_products.forEach((p, idx) => {
        const mixEl = document.getElementsByName('mix_percentage')[idx]
        const qtyEl = document.getElementsByName('quantity')[idx]

        mixEl?.setAttribute('disabled', '')
        qtyEl?.removeAttribute('disabled')

        if (qtyEl) qtyEl.style.color = 'blue'
        if (mixEl) mixEl.style.color = 'black'
      })
    })
  }

  const mixpercentage = () => {
    for (const info of inputList.product_information) {
      const nonFre = info.order_list_products.filter((p) => p.product_name !== 'FRE')

      if (nonFre.length <= 1) {
        continue
      }

      let sum = 0

      for (const p of nonFre) {
        if (p.mix_percentage === 0 || p.mix_percentage === null || p.mix_percentage === undefined) {
          swal.fire('Warning', 'The mix percentage cannot be empty', 'warning')
          return false
        }

        sum += Number(p.mix_percentage)
      }

      if (Math.abs(sum - 100) > 0.1) {
        swal.fire(
          'Warning',
          'The mix percentage for each product set should add up to approximately 100%',
          'warning',
        )
        return false
      }
    }

    return true
  }

  const ensureProductInformationForSubmit = () => {
    if (
      inputList.product_information.length === 0 ||
      inputList.product_information[0].order_list_products.length === 0
    ) {
      const nextInputList = {
        ...inputList,
        product_information: [{ docket_numbers: [], product_id: null, order_list_products: [] }],
      }

      setInputList(nextInputList)

      return nextInputList
    }

    return inputList
  }

  const submitOrders = async (e) => {
    e.preventDefault()

    if (!mixpercentage()) return
    if (!validateRequiredFields()) return

    try {
      const basePayload = id ? ensureProductInformationForSubmit() : inputList
      const payload = {
        ...basePayload,
        source_tab_id: getOrdersTabId(),
      }

      const res = id
        ? await axios.put(`/api/update_orders/${id}`, payload)
        : await axios.post(`/api/create_order`, payload)

      if (res.data.status === 200) {
        broadcastOrdersCacheMutation({
          type: id ? 'update' : 'create',
          id: res.data.id,
          patch: buildOrderCachePatch(res.data.id),
        })

        swal.fire('Success', res.data.message, 'success')
        setError({})
        setPristine()
        history.push(`/orders/update/${res.data.id}`)
        return
      }

      if (res.data.status === 400) {
        setError(normalizeErrors(res.data.errors || {}))
        await showValidationWarning()
        scrollToErrorSummary()
        return
      }

      if (res.data.status === 404) {
        swal.fire('Error', res.data.message, 'error')
        history.push('/orders')
        return
      }
    } catch (error) {
      console.error('Submit order failed:', error)

      if (error?.response?.status === 400 || error?.response?.data?.status === 400) {
        await handleValidationError(error)
        return
      }

      await swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Something went wrong while saving the order.',
      })
    }
  }

  const duplicateOrder = async (e) => {
    e.preventDefault()

    if (!mixpercentage()) return
    if (!validateRequiredFields()) return

    try {
      const profileRes = await axios.get(`/api/edit_profile`)

      const payload = {
        ...inputList,
        created_by: profileRes.data?.user?.id || inputList.created_by,
        user_name: profileRes.data?.user?.name || inputList.user_name,
        source_tab_id: getOrdersTabId(),
      }

      const res = await axios.post(`/api/create_order`, payload)

      if (res.data.status === 200) {
        const duplicatedOrderId = res.data.id
        const now = new Date().toISOString()

        const patch = {
          ...buildOrderCachePatch(duplicatedOrderId),
          id: duplicatedOrderId,
          created_at: now,
          updated_at: now,
        }

        broadcastOrdersCacheMutation({
          type: 'create',
          id: duplicatedOrderId,
          patch,
        })

        await swal.fire({
          icon: 'success',
          title: 'Success',
          text: res.data.duplicate || res.data.message || 'Order duplicated successfully',
        })

        setPristine()
        setError({})
        history.push(`/orders/update/${duplicatedOrderId}`)
        return
      }

      if (res.data.status === 400) {
        setError(normalizeErrors(res.data.errors || {}))
        await showValidationWarning()
        scrollToErrorSummary()
        return
      }
    } catch (error) {
      console.error('Duplicate order failed:', error)

      if (error?.response?.status === 400 || error?.response?.data?.status === 400) {
        await handleValidationError(error)
        return
      }

      await swal.fire({
        icon: 'error',
        title: 'Duplicate Failed',
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Something went wrong while duplicating the order.',
      })
    }
  }

  const closeTabOrRedirect = () => {
    window.close()

    setTimeout(() => {
      history.push('/orders')
    }, 300)
  }

  const saveAndClose = async (e) => {
    e.preventDefault()

    if (!mixpercentage()) return
    if (!validateRequiredFields()) return

    try {
      const basePayload = id ? ensureProductInformationForSubmit() : inputList
      const payload = {
        ...basePayload,
        source_tab_id: getOrdersTabId(),
      }

      const res = id
        ? await axios.put(`/api/update_orders/${id}`, payload)
        : await axios.post(`/api/create_order`, payload)

      if (res.data.status === 200) {
        broadcastOrdersCacheMutation({
          type: id ? 'update' : 'create',
          id: res.data.id,
          patch: buildOrderCachePatch(res.data.id),
        })

        setError({})
        setPristine()

        await swal.fire({
          icon: 'success',
          title: 'Success',
          text:
            res.data.message ||
            (id ? 'Order Updated Successfully' : 'Order Generated Successfully'),
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false,
        })

        closeTabOrRedirect()
        return
      }

      if (res.data.status === 400) {
        setError(normalizeErrors(res.data.errors || {}))
        await showValidationWarning()
        scrollToErrorSummary()
        return
      }

      if (res.data.status === 404) {
        await swal.fire({
          icon: 'error',
          title: 'Error',
          text: res.data.message || 'Order not found',
        })

        history.push('/orders')
        return
      }
    } catch (error) {
      console.error('Save and close failed:', error)

      if (error?.response?.status === 400 || error?.response?.data?.status === 400) {
        await handleValidationError(error)
        return
      }

      if (id && error?.response?.status === 502) {
        broadcastOrdersCacheMutation({
          type: 'update',
          id,
          patch: buildOrderCachePatch(id),
        })

        setError({})
        setPristine()

        await swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Order Updated Successfully',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false,
        })

        closeTabOrRedirect()
        return
      }

      await swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Something went wrong while saving the order.',
      })
    }
  }

  const handleCheckBox = (e) => {
    setDirty()

    if (Number(inputList.order_status) === 3) {
      swal.fire('Error', 'Cancelled orders cannot be changed until they are reopened.', 'error')
      return
    }

    const { name, checked } = e.target
    const nameTime = `${name}_time`
    const list = { ...inputList }

    const today = new Date()
    const MyDateString = `${today.getFullYear()}-${('0' + (today.getMonth() + 1)).slice(-2)}-${(
      '0' + today.getDate()
    ).slice(-2)}`

    const getTotalDockets = () => {
      let total = 0

      ;(inputList.product_information || []).forEach((info) =>
        (info.docket_numbers || []).forEach(() => total++),
      )

      return total
    }

    const tonsOk =
      inputList.tons_complete === true ||
      inputList.tons_complete === 1 ||
      inputList.tons_complete === '1'

    if (checked) {
      if (name === 'dockets_complete') {
        const total_rows = getTotalDockets()

        if (total_rows <= 0 || !tonsOk) {
          swal.fire({
            icon: 'warning',
            title: 'Warning',
            text: 'Minimum one docket should be added and tons complete should be checked',
          })

          return
        }
      }

      list[name] = '1'
      list[nameTime] = MyDateString

      setInputList(list)
      return
    }

    swal
      .fire({
        title: 'Are you sure you want to proceed?',
        text: 'Unchecking will reset the Date and Time and may affect Order Complete Status!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Proceed!',
      })
      .then((result) => {
        if (result.isConfirmed) {
          swal.fire('Unchecked!', 'Date and Time have been reset.', 'success')

          list[nameTime] = null
          list[name] = '0'

          if (name === 'dockets_complete') {
            list.order_status = 0
          }

          setInputList(list)
        } else {
          setInputList((prev) => ({ ...prev, [name]: '1' }))
        }
      })
  }

  const handleRadio = (e) => {
    setDirty()

    if (Number(inputList.order_status) === 3) {
      swal.fire('Error', 'Cancelled orders cannot be changed until they are reopened.', 'error')
      return
    }

    const { name, value } = e.target

    setInputList((prev) => ({ ...prev, [name]: value }))
  }

  const handleChangeProductMix = (e, index) => {
    setDirty()

    const { value } = e

    setInputList((prev) => ({
      ...prev,
      product_information: prev.product_information.map((info, i) =>
        i === index ? { ...info, product_id: value } : info,
      ),
    }))
  }

  const removeLevel3 = (e, i, j, arrayname, arrayname2) => {
    setDirty()
    e.preventDefault()

    const list = { ...inputList }
    list[arrayname][i][arrayname2].splice(j, 1)

    setInputList(list)
  }

  const handleChangeOtherLineItems = async (e, i) => {
    setDirty()

    const { name, value } = e.target
    const fuelLevyItem = lineItems.find((item) => item.id == value)

    if (name === 'type' && fuelLevyItem) {
      try {
        const response = await postXeroRequest('/api/xero_additional_line_items', {
          code: fuelLevyItem.xero_code,
        })

        const xeroDetails = response.data?.item?.body?.Items[0]

        if (xeroDetails) {
          const { Code, Description } = xeroDetails
          const list = { ...inputList }

          list.other_line_items[i][name] = value
          list.other_line_items[i].xero_code = Code || 'N/A'
          list.other_line_items[i].description = Description || 'N/A'

          const productLength = inputList.product_information[0].order_list_products.length
          const tonsActual = inputList.tons_actual
          const mixPrice = parseInt(mixSkuValue)
          const unitPrice = inputList.product_information[0].order_list_products[0].unit_price
          const result = productLength > 1 ? mixPrice : unitPrice

          list.other_line_items[i].amount = tonsActual * result || ''

          setInputList(list)
        }
      } catch (err) {
        const list = { ...inputList }

        list.other_line_items[i].xero_code = 'N/A'
        list.other_line_items[i].description = 'N/A'

        console.error('Failed to fetch price:', err)

        setInputList(list)
      }
    }

    const list = { ...inputList }
    list.other_line_items[i][name] = value

    setInputList(list)
  }

  const mixCodeValue = xeroCodeGeneratorMix()
  const mixSkuValue = xeroSkuGeneratorMix()

  if (loading) {
    return <Preloader />
  }

  return (
    <div className="content-wrapper">
      <nav aria-label="breadcrumb p-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <NavLink to="/">Dashboard</NavLink>
          </li>

          <li className="breadcrumb-item">
            <NavLink to="/orders">Open Orders</NavLink>
          </li>

          <li className="breadcrumb-item active" aria-current="page">
            {id ? 'Update order' : 'Add New Order'}
          </li>
        </ol>
      </nav>

      {Prompt}

      <h3 className="text-dark">{id ? 'Update Order' : 'Add New Order'}</h3>

      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6 p-0"></div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="card">
          <div className="card-header">
            <div className="d-flex flex-row justify-content-between">
              <div>
                <h5 className="card-title pt-2">{id ? 'Update Order' : 'Add Order'}</h5>
              </div>

              <div>
                <button type="button" className="btn btn-primary" onClick={submitOrders}>
                  {id != null ? 'Update Changes' : 'Save Changes'}
                </button>

                <button type="button" onClick={saveAndClose} className="btn btn-primary ms-3">
                  {id != null ? 'Update & Close Tab' : 'Save & Close Tab'}
                </button>

                {id != null && (
                  <button type="button" onClick={duplicateOrder} className="btn btn-info ms-3">
                    Duplicate & Open
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="card-body">
            <form onSubmit={submitOrders} id="product_form">
              <div className="card-body">
                {Object.keys(errorList || {}).length > 0 && (
                  <div className="alert alert-danger" data-error-summary="true">
                    <strong>Please fix the following:</strong>

                    <ul className="mb-0 mt-2">
                      {Object.entries(errorList).map(([field, message]) => (
                        <li key={field}>{message}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="row mb-3">
                  <OrderStatusSection
                    inputList={inputList}
                    orderStatus={OrderStatus}
                    errorList={errorList}
                    onCheck={handleCheckBox}
                    onRadioChange={handleRadio}
                    onCheckChange={handleCheckBox}
                    onCompleteToggle={HandleCompleted}
                    onCancelledToggle={HandleCancelled}
                    onDateChange={handleChangeLevel1}
                  />
                </div>

                <hr />

                <CustomerInfoSection
                  inputList={inputList}
                  customer={customer}
                  customerSelectOptions={customerSelectedOptions}
                  customerAddress={customerAddress}
                  addressSelect={addressSelect}
                  errorList={errorList}
                  onCustomerChange={handleChangeCustomerID}
                  onAddressChange={HandleChangeAddress}
                  onFieldChange={handleChangeLevel1}
                />

                <hr />

                <div className="row">
                  <OrderDetailsSection
                    inputList={inputList}
                    orderType={orderType}
                    subOrderType={subOrderType}
                    orderDeliveryZone={orderDeliveryZone}
                    skuLocation={skuLocation}
                    agents={agents}
                    machines={machines}
                    crops={crops}
                    operator={operator}
                    priceLoaded={priceLoaded}
                    errorList={errorList}
                    onFieldChange={handleChangeLevel1}
                    onXeroFieldChange={handleChangeLevel1XeroCode}
                    onOperatorChange={handleChangeLevel2}
                    onAddOperator={handleAddOperators}
                    onRemoveOperator={handleRemoveOperators}
                  />

                  <hr />
                </div>

                {errorList.product_information && (
                  <span className="text-danger">{errorList.product_information}</span>
                )}

                <div className="d-flex justify-content-between"></div>

                <ProductInfoSection
                  inputList={inputList}
                  productMixList={productMixList}
                  spreadChargeRates={spreadchargerates}
                  quantityType={quantityType}
                  skuLocation={skuLocation}
                  orderTypeName={orderTypeName}
                  xeroItemCodeStatus={xeroItemCodeStatus}
                  mixCodeValue={mixCodeValue}
                  mixSkuValue={mixSkuValue}
                  priceLoaded={priceLoaded}
                  errorList={errorList}
                  onFieldChange={handleChangeLevel1}
                  onTonsKeyUp={percentatgeCalc}
                  onTonsBlur={handleBlurDecimalLevel1}
                  onProductMixChange={handleChangeProductMix}
                  onAddProduct={handleAddNewProduct}
                  onRemoveProduct={removeLevel3}
                  onQuantityChange={handleChangeLevel3}
                  onSpreadRateChange={handleChangeLevel3XeroCode}
                  onQuantityTotal={quantityTotal}
                  onQuantityTotalBlur={quantityTotalBlur}
                  onPercentatgeCalcBlur={percentatgeCalcBlur}
                  onEnableMix={enableMix}
                  onEnableQuantity={enableQuantity}
                  onAddDocket={handleAddDocket}
                  onRemoveDocket={handleRemoveDocket}
                  onFileChange={handleImage}
                />

                <hr />

                <div className="d-flex justify-content-between">
                  <AdditionalLineItemsSection
                    items={inputList.other_line_items}
                    lineItems={lineItems}
                    skuLocation={skuLocation}
                    errorList={errorList}
                    onAdd={addLineItems}
                    onRemove={removeLineItems}
                    onChange={handleChangeOtherLineItems}
                    onBlurDecimal={handleBlurDecimalLevel2}
                  />
                </div>

                <div>
                  <button type="submit" className="btn btn-primary">
                    {id != null ? 'Update Changes' : 'Save Changes'}
                  </button>

                  <button type="button" onClick={saveAndClose} className="btn btn-primary ms-3">
                    {id != null ? 'Update & Close Tab' : 'Save & Close Tab'}
                  </button>

                  {id != null && (
                    <button type="button" onClick={duplicateOrder} className="btn btn-info ms-3">
                      Duplicate & Opens
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Create
